(ns app.front.ui.select
  (:require [app.front.ui.util :as uiu]
            [app.front.util :as futil]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.util.string :as str-util]
            [schema-tools.core :as st]))

(defn- log-label-warning [id]
  (when ^boolean js/goog.DEBUG
    (str "label missing for select id " id ", please add one")))

(defn select
  "Make a select component that operates on a sequence of arbitrary values (not just
  strings).

  Nil represents empty value and is permitted. Props are passed through to `cc/select`

  Required
   `:id`, `:required`, `:values`, `val->str`, `:on-change`, `:value`

  Optional
   `:allow-empty?`    (boolean, default true)
   `:select-text`     (string, default nil)
   `:disabled-values` (set, default nil)
   `:label-text`      (string), REQUIRED if select-text or aria-labelledby not provided
   `:grouper`         (function item -> title for creating groups)
   `:inline-label?`   (bool) if true, label will be on the same line as the select, with bolder font
   `:hide-label`      (bool) hide label visually, but keep it in DOM for accessibility
   `:aria-labelledby` (id), id of some external element that labels this select
   `:external-label?` (bool) external label exists for given id, do not create label at all"
  [{:keys [values
           grouper
           val->str
           on-change
           value
           allow-empty?
           inline-label?
           empty-text
           disabled-values
           select-text
           label-text
           aria-labelledby
           external-label?
           required
           tr
           id
           val->token]
    :as   props
    :or   {allow-empty? true
           select-text  nil
           val->str     str}}]
  (let [token->value      (->> values
                               (uiu/make-token->value (or (not value) allow-empty?) val->token))
        grouped           (->> token->value
                               (group-by (comp (or grouper (constantly nil))
                                               :value
                                               second)))
        curr-token        (uiu/get-token token->value value)
        tr                (or tr
                              ;; fallback to subscription for backwards compatibility
                              (<sub [:tr]))
        label-or-nil      (str-util/blank->nil label-text)
        use-inline-label? (and inline-label? label-or-nil)
        label-id          (when use-inline-label?
                            (str id "-label"))
        ;; Create additional label props based on the props provided. These won't apply if `:label-text` is provided.
        label-props       (cond
                            external-label?
                            {:no-label true}

                            ;; labelled with the span created below, no <label>
                            use-inline-label?
                            {:no-label        true
                             :aria-labelledby label-id}

                            ;; labelled with something external, no <label>
                            aria-labelledby
                            {:no-label true}

                            ;; not visually labelled, <label> contains the select text for accessibility
                            (and (nil? label-or-nil) select-text)
                            {:label-text select-text
                             :hide-label true}

                            ;; nothing usable provided for label, use a default value in hidden <label> and warn
                            (and (nil? label-or-nil) (nil? select-text))
                            (do (log-label-warning id)
                                {:label-text (tr [:select/value])
                                 :hide-label true}))
        disabled-tokens   (some->> disabled-values
                                   ;; do not disable empty value when allow-empty? is true
                                   (uiu/make-token->value (not allow-empty?) val->token)
                                   (map first)
                                   set)]
    [(if use-inline-label?
       :div.flex.items-baseline.gap-3.select-container
       :<>)
     (when use-inline-label?
       [:span.font-bold.uppercase.text-ui-night-sky
        {:id label-id}
        label-text])
     (cc/select
       (cond-> (merge (-> (set/rename-keys props {:class   :class-name
                                                  :inline? :inline})
                          (dissoc :no-label)
                          (update :disabled boolean))
                      label-props
                      {:value     (or curr-token "")
                       :on-change #(-> % futil/tval
                                       token->value
                                       :value
                                       on-change)})
         required (update :class-name str " required-select")
         :always (st/select-schema cc/SelectOptions))
       (futil/for-frag [[group-title token->value] (sort-by first grouped)]
         (into (if group-title
                 [cc/select-item-group {:label group-title}]
                 [:<>])
               (for [[token {:keys [value]}] (uiu/sort-tokens token->value)]
                 (cc/select-item {:text     (or (when (some? value) (val->str value))
                                                select-text
                                                (if allow-empty?
                                                  (or empty-text "")
                                                  (tr [:select-option])))
                                  :disabled (contains? disabled-tokens token)
                                  :value    token})))))]))
